import { useQueryClient } from "@tanstack/react-query";
import { useOptimisticUpdateMutation } from "../../../hooks/useOptimisticUpdateMutation";
import ComponentsService from "../../../services/ComponentsService";
import {
  ComponentTemplateBriefDto,
  ComponentTemplateDto,
  PaginatedListOfComponentTemplateBriefDto,
  PaginatedListOfComponentTemplateDto,
} from "@growth-machine-llc/stridist-api-client";
import { COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY } from "../../component-templates-library/ComponentTemplateLibrary";
import {
  COMPONENT_TEMPLATE_QUERY_KEY,
  COMPONENT_TEMPLATES_LIST_QUERY_KEY,
} from "../ComponentTemplateDialog";

export const useUpdateComponentTemplateMutation = (templateId: string) => {
  const queryClient = useQueryClient();

  return useOptimisticUpdateMutation({
    successToastMessage: "Template updated.",
    queryKey: [COMPONENT_TEMPLATE_QUERY_KEY, { templateId }],
    mutationFn: ComponentsService.updateComponentTemplate,
    optimisticUpdater: {
      updateFn: (oldData: ComponentTemplateDto, newData, _) => {
        const updatedData = ComponentTemplateDto.fromJS({
          ...oldData,
          ...newData,
        });
        return updatedData;
      },
    },
    options: {
      onSuccess: (_, variables) => {
        queryClient.setQueryData(
          [COMPONENT_TEMPLATES_LIST_QUERY_KEY],
          (oldData: PaginatedListOfComponentTemplateBriefDto) => ({
            ...oldData,
            items: oldData?.items?.map((item) =>
              item.id === templateId
                ? ComponentTemplateBriefDto.fromJS({
                    ...item,
                    title: variables.title,
                  })
                : item,
            ),
          }),
        );
        queryClient
          .getQueryCache()
          .findAll({
            queryKey: [COMPONENT_TEMPLATES_LIBRARY_QUERY_KEY],
            exact: false,
          })
          .forEach((query) => {
            queryClient.setQueryData(
              query.queryKey,
              (oldData: PaginatedListOfComponentTemplateDto | undefined) => {
                if (!oldData) return oldData;
                return {
                  ...oldData,
                  items: oldData.items.map((item) =>
                    item.id === templateId
                      ? ComponentTemplateDto.fromJS({
                          ...item,
                          ...variables,
                        })
                      : item,
                  ),
                } as PaginatedListOfComponentTemplateDto;
              },
            );
          });
      },
    },
  });
};
